import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Select, Skeleton, Space } from 'antd';
import classNames from 'classnames/bind';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _ from 'lodash';
import { Link, useParams } from 'react-router-dom';

import { ReactComponent as SelectIcon } from 'helpers/icons/SelectArrow.svg';
import ENGLISH_KNOWLEDGE_STATES, { ENGLISH_KNOWLEDGE_TITLE } from 'helpers/constants/Skills/EnglishKnowledgeStates';
import { ExperienceFilterNames } from 'helpers/types/Skills';
import EXPERIENCE_FILTERS from 'helpers/constants/Skills/ExpFilters';
import { PATH_FIND_SPECIALISTS } from 'helpers/constants/_common/MenuCases';
import { ReactComponent as SearchIcon } from 'helpers/icons/SearchIcon.svg';
import OCCUPATIONS from 'helpers/constants/Occupations/Occupations';
import { ReactComponent as CloseCross } from 'helpers/icons/CloseCross18.svg';
import { ReactComponent as ShowMore } from 'helpers/icons/Buttons/ShowMore.svg';
import { ReactComponent as ShowLess } from 'helpers/icons/Buttons/ShowLess.svg';
import selects from 'helpers/styles/components/selects.module.sass';
import inputs from 'helpers/styles/components/inputs.module.sass';
import buttons from 'helpers/styles/components/buttons.module.sass';
import { ConstantType, EnglishLevelEnum } from 'helpers/types/_common';
import { SKILL_EXPERIENCES_COLOR } from 'helpers/constants/Skills/Experience';
import { ReactComponent as EnglishIcon } from 'helpers/icons/EnglishNew.svg';

import EngKnowledgeLevel from '../../../../../../../_shared/EngKnowledgeLevel';
import SkillTag from '../../../../../../../_shared/SkillTag';
import { SpecialistsSkills, SpecialistsURLParams } from '../../../../../../helpers/types';
import SpecifyExpButton from '../../../../../SpecifyExpButton';
import RequestExperienceField from './components/RequestExperienceField';
import { RequestSettingsProps } from './helpers/types';
import S from './helpers/styles.module.sass';
import GeneralFiltersButton from '../../../../../../../GeneralFiltersButton/GeneralFiltersButton';

const cx = classNames.bind(S);

const RequestSettings: React.FC<RequestSettingsProps> = (props) => {
  const {
    loading, onChangeExperience, settings, onExperienceBlur,
    onChangeEnglishKnowledge, isViewMode, onChangeValue,
    onClear,
  } = props;

  const inputRef = useRef<Input>(null);

  const { tab } = useParams<SpecialistsURLParams>();

  const [ specifiedExperiences, changeExpFilters ] = useState<(ExperienceFilterNames | null)[]>([ ExperienceFilterNames.notSet ]);
  const [ isCollapsed, setIsCollapsed ] = useState(false);

  useEffect(() => {
    const experienceKeys = _.map(settings.skills, s => s.experience);

    const sortedExperienceKeys = _.reduce(ExperienceFilterNames, (acc, value) => {
      if (_.includes(experienceKeys, value) || _.includes(specifiedExperiences, value)) {
        return [ ...acc, value ];
      }

      return acc;
    }, [] as ExperienceFilterNames[]);

    const uniqExpKeys = _.uniq([ ExperienceFilterNames.notSet, ...sortedExperienceKeys ]);

    changeExpFilters(uniqExpKeys);
  }, [ settings.skills ]);

  const specifyExperienceFilter = (filterKey: ExperienceFilterNames) => {
    if (specifiedExperiences.length === Object.keys(EXPERIENCE_FILTERS).length) return;

    const expKeys = [ ...specifiedExperiences, filterKey ];

    const sortedExperienceKeys = _.reduce(ExperienceFilterNames, (acc, value) => {
      if (_.includes(expKeys, value)) {
        return [ ...acc, value ];
      }
      return acc;
    }, [] as ExperienceFilterNames[]);

    changeExpFilters(sortedExperienceKeys);
  };

  const removeExperienceFilter = (filterKey: ExperienceFilterNames | number) => {
    if (_.isNumber(filterKey)) {
      changeExpFilters([ ...specifiedExperiences.filter((f, i) => i !== filterKey) ]);
    } else {
      const keysToRemove = _.reduce(settings.skills, (acc, v, key) => {
        if (filterKey === v.experience) {
          return [ ...acc, key ];
        }

        return acc;
      }, [] as string[]);

      const updatedExpSkills: SpecialistsSkills = _.reduce(settings.skills, (acc, v, key) => {
        if (_.includes(keysToRemove, key)) {
          return acc;
        }

        return {
          ...acc,
          [key]: v,
        };
      }, {});

      const updatedExpKeys = _.filter(specifiedExperiences, f => f !== filterKey);

      changeExpFilters(updatedExpKeys);
      onExperienceBlur(updatedExpSkills);
    }
  };

  const renderKnowledgeSelect = () => {
    const options = _.map(ENGLISH_KNOWLEDGE_STATES, v => <Select.Option key={v.knowledgeId} value={v.knowledgeId}>{v.secondName || v.name}</Select.Option>);

    return (
      <Select
        value={settings?.requestData?.englishKnowledge}
        className={cx(selects.qsSelect, 'specialists-settings_select')}
        dropdownClassName={selects.selectDropdown}
        onChange={onChangeEnglishKnowledge}
        suffixIcon={<SelectIcon />}
        size="large"
        bordered={false}
        disabled={isViewMode}
      >
        {options}
      </Select>
    );
  };

  const renderSearchInput = () => {
    const onChangeInput = _.debounce((event: React.ChangeEvent<HTMLInputElement>) => {
      onChangeValue({ searchValue: event.target.value });
    }, 1000);

    return (
      <Input
        ref={inputRef}
        className={cx(inputs.qsInput, 'specialists-settings_input')}
        prefix={<SearchIcon style={{ fill: '#CCCCCC' }} />}
        size="large"
        onChange={onChangeInput}
        allowClear
        defaultValue={settings.searchValue}
        placeholder="Search by name..."
      />
    );
  };

  const renderAllExperienceFields = () => specifiedExperiences.map((filterKey, index) => renderExperienceField(filterKey, index));

  const renderExperienceField = (currentExpFilterKey: ExperienceFilterNames | null, fieldIndex: number) => (
    <RequestExperienceField
      key={currentExpFilterKey || fieldIndex}
      specifiedExperiences={specifiedExperiences}
      currentExpFilterKey={currentExpFilterKey}
      fieldIndex={fieldIndex}
      settings={settings}
      onExperienceBlur={onExperienceBlur}
      removeExperienceFilter={removeExperienceFilter}
      onChangeExperience={onChangeExperience}
      changeExpFilters={changeExpFilters}
      isViewMode={isViewMode}
    />
  );

  const renderRequestTitle = () => {
    if (loading) {
      return <Skeleton.Input style={{ width: 400 }} active />;
    }

    if (!settings) {
      return <span className={cx('specialists-settings_filters_project-title')}>Unknown</span>;
    }

    return (
      <span className={cx('specialists-settings_filters_project-title')}>
        <span className={cx('project-title_bold')}>
          {settings?.requestData?.occupation ? OCCUPATIONS[settings.requestData.occupation]?.name : 'Unknown'}
        </span>
        <span className={cx('project-title_text')}>
          {` for ${settings?.requestData?.partner?.name || 'Unknown'}: ${settings?.requestData?.activity?.name || 'Unknown'}`}
        </span>
      </span>
    );
  };

  const renderSpecifyExpButton = () => {
    const lastExpDone = specifiedExperiences.length !== Object.keys(ExperienceFilterNames).length;

    if (!lastExpDone) {
      return;
    }

    return (
      <SpecifyExpButton
        specifiedExperiences={specifiedExperiences}
        specifyExperienceFilter={specifyExperienceFilter}
        isViewMode={isViewMode}
      />
    );
  };

  const renderClearAll = () => {
    const disabled = _.isEmpty(settings.skills)
    && settings.requestData?.englishKnowledge === EnglishLevelEnum.Zero
    && _.isEmpty(settings.searchValue);

    const onClickClear = () => {
      changeExpFilters([ ExperienceFilterNames.notSet ]);
      onClear();
      if (inputRef && inputRef.current) {
        inputRef.current.setValue('');
      }
    };

    return (
      <Button
        type="text"
        size="large"
        className={cx('clear-button')}
        onClick={onClickClear}
        disabled={disabled}
      >
        <div className={buttons.iconTextWrapper}>
          <CloseCross className={cx('clear-button_icon', { disabled })} />
          <span>Clear Filters</span>
        </div>
      </Button>
    );
  };

  const renderCollapseButton = () => (
    <Button
      type="text"
      className={cx('collapse-button')}
      onClick={() => setIsCollapsed(!isCollapsed)}
    >
      <Space>
        {isCollapsed ? <ShowMore className={cx('collapse-icon')} /> : <ShowLess className={cx('collapse-icon')} /> }
        <span>{ isCollapsed ? 'Expand' : 'Collapse' }</span>
      </Space>
    </Button>
  );

  const renderCollapsedSkills = () => {
    const skills = _.map(settings.skills, s => <SkillTag title={s.name} key={s.name} color={SKILL_EXPERIENCES_COLOR[s.experience]} />);

    const englishTitles: ConstantType<string> = {
      ...ENGLISH_KNOWLEDGE_TITLE,
      Zero: 'Any',
    };

    return (
      <Space split={<div className={cx('splitter')} />}>
        <div className={cx('collapsed-skills', { empty: _.isEmpty(skills) })}>
          {_.isEmpty(settings.skills) ? 'No selected skill tags' : skills}
        </div>
        <Space className={cx('collapsed-english')}>
          <EngKnowledgeLevel englishKnowledgeLevel={settings?.requestData?.englishKnowledge} zeroEnglishPlaceholder={<EnglishIcon viewBox="0 0 24 19" />} />
          <span>{ settings?.requestData?.englishKnowledge && englishTitles[settings?.requestData?.englishKnowledge] }</span>
        </Space>
      </Space>
    );
  };

  const renderExpandedSettings = () => (
    <>
      <div className={cx('specialists-settings_filters')}>
        <Space align="center" split={<div className={cx('splitter')} />}>
          <Link to={`${PATH_FIND_SPECIALISTS}/${tab}`} className={cx('specialists-settings_filters_go-back')}>
            <FontAwesomeIcon icon="arrow-left" />
          </Link>

          {renderRequestTitle()}
        </Space>
        <Space>
          <GeneralFiltersButton
            buttonClassName={S.generalFiltersButton}
            onSearch={ids => onChangeValue(ids)}
          />

          {renderKnowledgeSelect()}
          {renderSearchInput()}
          {renderClearAll()}
          {renderCollapseButton()}
        </Space>
      </div>

      <div className={cx('specialists-settings_experience')}>
        <div className={cx('specialists-settings_experience-inner')}>
          {renderAllExperienceFields()}
        </div>
        { renderSpecifyExpButton() }
      </div>
    </>
  );

  const renderCollapsedSettings = () => (
    <div className={cx('specialists-settings_filters', { collapsed: isCollapsed })}>
      <div className={cx('settings-title_wrapper')}>
        <Space align="center" split={<div className={cx('splitter')} />}>
          <Link to={`${PATH_FIND_SPECIALISTS}/${tab}`} className={cx('specialists-settings_filters_go-back')}>
            <FontAwesomeIcon icon="arrow-left" />
          </Link>

          {renderRequestTitle()}
        </Space>

        {renderCollapseButton()}
      </div>
      <div>
        {renderCollapsedSkills()}
      </div>
    </div>
  );

  return (
    <div className={cx('specialists-settings', { collapsed: isCollapsed })}>
      { isCollapsed ? renderCollapsedSettings() : renderExpandedSettings() }
    </div>
  );
};

export default RequestSettings;
